import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import { FulfillmentFlatTeamSettingGroupById } from "./groups";

export type FulfillmentFlatTeamSettingKey =
  | "outboundLabels/defaultShipFromLocationExternalId"
  | "outboundLabels/address/internationalAddressOutreach";

const shipFromLocationKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/defaultShipFromLocationExternalId";
const shipFromLocationGroupId = "fulfillment.ship_from_locations" as const;

const internationalAddressOutreachKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/address/internationalAddressOutreach";
const addressGroupId = "fulfillment.address" as const;

export const FulfillmentFlatTeamSettingCatalogDefinitions = [
  {
    id: shipFromLocationKey,
    groupId: shipFromLocationGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default ship from location",
        description:
          "Selects the ship-from location used automatically when creating a manual order.",
        synonyms: [
          "default ship from location",
          "default warehouse",
          "manual order location",
          "shipping origin default",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[shipFromLocationGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: shipFromLocationKey },
  },
  {
    id: internationalAddressOutreachKey,
    groupId: addressGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Include international orders",
        description:
          "Applies address verification failure handling to orders shipping outside the US. When on, international orders get the same correction emails and holds as domestic ones; when off, Redo takes no action on an international address it could not verify. Off by default because international addresses verify less reliably, so turning it on increases both emails and holds.",
        synonyms: [
          "international address verification",
          "international address emails",
          "non-US addresses",
          "overseas orders address",
          "address outreach international",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[addressGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: internationalAddressOutreachKey,
    },
  },
] satisfies readonly SettingDefinition<
  FulfillmentFlatTeamSettingKey,
  typeof shipFromLocationGroupId | typeof addressGroupId,
  TeamSettingsSettingValueSource<FulfillmentFlatTeamSettingKey>
>[];

export const FulfillmentFlatTeamSettingMetadataByKey = Object.fromEntries(
  FulfillmentFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<FulfillmentFlatTeamSettingKey, SettingMetadata>;
